import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import numeral from 'numeral';

import { useTheme } from '@material-ui/core/styles';

import { showSuccessSnackbar } from 'modules/snackbar';
import { paramConverter } from 'modules/common';
import { makeRowColumn, getHeadObj } from 'lib/commonLib';
import { cwList } from 'lib/api/time';
import NewInput from 'components/atoms/NewInput';

import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import AdverIdList from 'components/molecules/AdverIdList';
import NewCommonFilter from 'components/organisms/NewCommonFilter';
import FilterCollection from 'components/organisms/FilterCollection';

import CwAnalysisGraph from './CwAnalysisGraph';

const CwAnalysis = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  let initParams = {
    sort: 'desc',
    order: 'STATS_DTTM',
    sDate: moment().add(-30, 'day').format('YYYYMMDD'),
    eDate: moment().format('YYYYMMDD'),
    advrtsPrdtCode: 'all',
    bdgtType: 'all',
    adverId: null,
    exAdverIds: '',
  };
  const [initialization, setInitialization] = useState(false);
  const [params, setParams] = useState(initParams);
  const [loading, setLoading] = useState(true);
  const filterList = {
    advrtsPrdtCode: [
      { value: 'all', label: t('전체') },
      { value: '01', label: t('배너') },
      { value: '03', label: t('아이커버') },
      { value: '02', label: t('브랜드링크') },
      { value: '07', label: t('네이티브') },
    ],
    bdgtType: [
      { value: 'all', label: t('전체') },
      { value: '01', label: t('제한') },
      { value: '02', label: t('무제한') },
    ],
  };

  const customized = {
    PAR_EPRS_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'PAR_EPRS_CNT');
        return makeRowColumn(
          rowObj['PAR_EPRS_CNT'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
      bodyStyle: {
        textAlign: 'center',
      },
    },
    CLICK_CNT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CLICK_CNT');
        return makeRowColumn(
          rowObj['CLICK_CNT'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
      bodyStyle: {
        textAlign: 'center',
      },
    },
    CLICK_RATE: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'CLICK_RATE');
        return makeRowColumn(
          rowObj['CLICK_RATE'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
      bodyStyle: {
        textAlign: 'center',
      },
    },
    ADVRTS_AMT: {
      makeBody: (rowObj, headsMap) => {
        const bodyItem1 = getHeadObj(headsMap, 'ADVRTS_AMT');
        return makeRowColumn(
          rowObj['ADVRTS_AMT'],
          bodyItem1.type,
          bodyItem1.format
        );
      },
      bodyStyle: {
        textAlign: 'center',
      },
    },
    BDGT_AMT: {
      makeBody: (rowObj, headsMap) => {
        return numeral(rowObj['BDGT_AMT']).format();
      },
      bodyStyle: {
        color: theme.palette.common.green,
      },
    },
    LIMIT_ADVER_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {t('광고주수')}
            <br />({t('예산제한')}/{t('무제한')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <>
            {numeral(
              rowObj['LIMIT_ADVER_CNT'] + rowObj['UN_LIMIT_ADVER_CNT']
            ).format()}
            <br />({numeral(rowObj['LIMIT_ADVER_CNT']).format()}
            {' / '}
            {numeral(rowObj['UN_LIMIT_ADVER_CNT']).format()})
          </>
        );
      },
    },
    UN_LIMIT_ADVER_CNT: {
      merged: true,
    },
    PARAM_PC_CNT: {
      makeHead: (headsMap) => {
        return (
          <>
            {t('장바구니 모수')}
            <br />({t('PC')}/{t('모바일')})
          </>
        );
      },
      makeBody: (rowObj, headsMap) => {
        return (
          <>
            {numeral(
              Number(rowObj['PARAM_PC_CNT']) +
                Number(rowObj['PARAM_MOBILE_CNT'])
            ).format()}
            <br />({numeral(rowObj['PARAM_PC_CNT']).format()}
            {' / '}
            {numeral(rowObj['PARAM_MOBILE_CNT']).format()})
          </>
        );
      },
    },
    PARAM_MOBILE_CNT: {
      merged: true,
    },
  };

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
      setLoading(true);
    }
  };

  /**
   * 필터 초기화
   * initialization 필터 내부에서 초기화 버튼이 클릭되었을 때 기본값으로 돌려주는 함수를 실행시킴.
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
    }
  };

  const handleTableCallBack = (state) => {
    if (!!state) {
      setLoading(false);
    }
  };

  /**
   * 스낵바 리덕스 값을 변경해주는 함수
   * @type {(...args: any[]) => any}
   */
  const handleClickSnackbar = useCallback((snackbarOptions) => {
    console.log(snackbarOptions);
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <NewFilterWrap loading={loading} handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={params}
          getParam={getParam}
          usePltfomTpCodeFilter={false}
          useDisplayFilter={false}
          useExternalFilter={false}
          toolTip={t('광고주ID 혹은 제외광고주 둘중 하나만 사용이 가능합니다.')}
        />
        <FilterCollection
          label={t('광고상품')}
          name="advrtsPrdtCode"
          params={params}
          value={params.advrtsPrdtCode}
          type="select"
          list={filterList.advrtsPrdtCode}
          callBack={getParam}
          labelKey="label"
          valueKey="value"
        />
        <AdverIdList
          params={params}
          getParam={getParam}
          disabled={!!params.exAdverIds}
        />
        <NewInput
          label={t('제외 광고주')}
          type="filter"
          name="exAdverIds"
          params={params}
          value={params['exAdverIds']}
          callBack={getParam}
          disabled={!!params.adverId}
        />
        <FilterCollection
          label={t('예산구분')}
          name="bdgtType"
          params={params}
          value={params.bdgtType}
          type="radio"
          list={filterList.bdgtType}
          callBack={getParam}
          labelKey="label"
          valueKey="value"
        />
      </NewFilterWrap>
      <CwAnalysisGraph
        params={{
          ...paramConverter(params),
          exAdverIds: params.exAdverIds === '' ? null : params.exAdverIds,
        }}
        handleClickSnackbar={handleClickSnackbar}
      />
      <NewTableContainer
        dataReqPromise={cwList}
        params={{
          ...params,
          exAdverIds: params.exAdverIds === '' ? null : params.exAdverIds,
        }}
        getParam={getParam}
        useSearch={false}
        customized={customized}
        callBack={handleTableCallBack}
      />
    </>
  );
};

export default withRouter(CwAnalysis);
